import React from 'react';

export const PrivacyPolicyEn: React.FC = () => (
  <>
    <section>
      <h2>1. Data controller</h2>
      <p>
        <strong>Leonardo Ferreira</strong>, acting as a sole trader operating the Roadbase platform
      </p>
      <p>Portuguese tax number (NIF) 305516760</p>
      <p>Praça Nove de Abril, 123, 4200-422 Porto, Portugal</p>
      <p>
        Email: <a href="mailto:info@roadbase.eu">info@roadbase.eu</a>
      </p>
      <p>
        For any question about personal data, including to exercise your rights, contact us at the
        address above.
      </p>
    </section>

    <section>
      <h2>2. What we collect</h2>
      <p>
        <strong>Account data.</strong> Name, email address, company name and, where provided, phone
        number. Passwords are stored only as a cryptographic hash — we have no access to your
        password in readable form.
      </p>
      <p>
        <strong>Quote request data.</strong> Origin and destination (city, country, postal code),
        cargo description, dimensions, weight, required equipment type, loading and delivery
        windows, notes, and any files or images you attach.
      </p>
      <p>
        <strong>Technical data.</strong> IP address, access date and time, browser type and usage
        logs, collected automatically by our servers.
      </p>
      <p>
        <strong>Communications.</strong> The content of messages you send us by email or through the
        platform.
      </p>
      <p>
        We do not collect special categories of personal data (health, beliefs, biometrics) or data
        relating to minors. The platform is intended exclusively for professional users.
      </p>
    </section>

    <section>
      <h2>3. Why we use it, and on what legal basis</h2>
      <table>
        <thead>
          <tr>
            <th>Purpose</th>
            <th>Legal basis</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Creating and managing your account</td>
            <td>Performance of a contract (Art. 6(1)(b) GDPR)</td>
          </tr>
          <tr>
            <td>Processing quote requests and contacting carriers on your behalf</td>
            <td>Performance of a contract or pre-contractual steps</td>
          </tr>
          <tr>
            <td>Sending operational emails (request confirmation, quotes received, status changes)</td>
            <td>Performance of a contract</td>
          </tr>
          <tr>
            <td>Platform security, abuse prevention, access logging</td>
            <td>Legitimate interests (Art. 6(1)(f))</td>
          </tr>
          <tr>
            <td>Improving the service based on aggregate usage</td>
            <td>Legitimate interests</td>
          </tr>
          <tr>
            <td>Business-to-business commercial communication</td>
            <td>Legitimate interests, with a right to object at any time</td>
          </tr>
          <tr>
            <td>Meeting legal and accounting obligations</td>
            <td>Legal obligation (Art. 6(1)(c))</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section>
      <h2>4. Who we share it with</h2>
      <p>
        <strong>Carriers.</strong> To obtain quotes, we pass carriers the{' '}
        <strong>characteristics of the cargo and route</strong> — origin, destination, dimensions,
        weight, equipment and dates. <strong>We do not pass on your identity or contact details</strong>{' '}
        unless you expressly authorise it. If negotiations proceed, the transport contract is
        entered into directly between you and the carrier, and from that point any data shared
        between you is outside our control.
      </p>
      <p>
        <strong>Processors.</strong> We use the following providers, who process data on our behalf
        and under written instructions:
      </p>
      <table>
        <thead>
          <tr>
            <th>Provider</th>
            <th>Function</th>
            <th>Location</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Hostinger</td>
            <td>Hosting of the server running the application, database and files</td>
            <td>Germany (EU)</td>
          </tr>
          <tr>
            <td>Resend</td>
            <td>Transactional email delivery</td>
            <td>Ireland (EU)</td>
          </tr>
          <tr>
            <td>Google (Drive)</td>
            <td>Encrypted backup repository</td>
            <td>United States</td>
          </tr>
          <tr>
            <td>HubSpot</td>
            <td>Commercial contact management and prospecting (does not process platform data)</td>
            <td>United States</td>
          </tr>
        </tbody>
      </table>
      <p>
        The database and the files you send us are held on our own server, not with third-party
        services. Files are stored in separate areas by type: client attachments, proposal PDFs and
        internal materials.
      </p>
      <p>
        <strong>Authorities.</strong> Where we are legally required to do so.
      </p>
      <p>
        We do not sell personal data and do not disclose it to third parties for advertising
        purposes.
      </p>
    </section>

    <section>
      <h2>5. Transfers outside the European Economic Area</h2>
      <p>
        The server, database, files and email delivery are entirely within the European Union. Only
        two things leave the EU: backups, held on Google Drive, and commercial contacts managed in
        HubSpot. These transfers rely on the Standard Contractual Clauses approved by the European
        Commission and, where applicable, on the provider's certification under the EU-U.S. Data
        Privacy Framework.
      </p>
      <p>
        Backups are encrypted on our server <strong>before</strong> transfer and can only be read
        with a key that remains under our control. The provider hosting them has no access to their
        contents.
      </p>
    </section>

    <section>
      <h2>6. How long we keep it</h2>
      <p>Retention has two independent layers: the live system and backups.</p>

      <h3>In the live system</h3>
      <table>
        <thead>
          <tr>
            <th>Data</th>
            <th>Retention</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Files and images you attach to a request</td>
            <td>Deleted around 3 months after the request is created</td>
          </tr>
          <tr>
            <td>Proposal PDFs generated by Roadbase</td>
            <td>Deleted when the proposal expires; a regenerated PDF expires after 3 days</td>
          </tr>
          <tr>
            <td>Account and profile</td>
            <td>While the account is active</td>
          </tr>
          <tr>
            <td>Quote requests, proposals and usage records</td>
            <td>
              For as long as needed for the purposes in this policy — in particular keeping your
              history, answering questions about earlier requests, and defending legal claims
            </td>
          </tr>
          <tr>
            <td>Records with tax relevance</td>
            <td>10 years, as required by law</td>
          </tr>
        </tbody>
      </table>
      <p>
        You may request deletion of your data at any time at{' '}
        <a href="mailto:info@roadbase.eu">info@roadbase.eu</a>. We remove it from the live system
        following the request.
      </p>

      <h3>In backups</h3>
      <p>
        Backups are encrypted snapshots kept solely for recovery in the event of system failure.
        They are not consulted in day-to-day operation and are not used for any other purpose.
      </p>
      <ul>
        <li>
          The <strong>daily</strong> backup covers the database and server configuration. It does
          not include the files you send us.
        </li>
        <li>
          The <strong>weekly</strong> backup covers everything in the daily one plus files, proposal
          PDFs and internal materials. It is the recovery point in the event of total server
          failure.
        </li>
      </ul>
      <p>
        Rotation is automatic and keeps, per backup group, up to 7 daily, 5 weekly and 12 monthly
        snapshots.
      </p>
      <p>
        One consequence is worth knowing:{' '}
        <strong>
          data deleted from the live system may remain in encrypted historical snapshots for up to
          around 12 months
        </strong>
        , after which rotation removes it automatically. In the meantime it exists only in encrypted
        copies and is not processed again. Should a backup ever be restored, deletion requests
        received in the meantime are re-applied.
      </p>
      <p>
        Our recovery procedure is designed to restore the whole system after a serious failure. We
        do not have a mechanism to selectively extract or erase a single file or record inside a
        snapshot once created.
      </p>
    </section>

    <section>
      <h2>7. Your rights</h2>
      <p>
        You have the right to access your data, to have it corrected, to request its deletion, to
        restrict or object to processing, and to receive it in a structured, commonly used format
        (portability). Where processing is based on consent, you may withdraw it at any time,
        without affecting the lawfulness of processing carried out before withdrawal.
      </p>
      <p>
        To exercise any of these rights, write to{' '}
        <a href="mailto:info@roadbase.eu">info@roadbase.eu</a>. We respond within one month.
      </p>
      <p>
        If you believe your data is not being handled properly, you may lodge a complaint with the
        Portuguese supervisory authority,{' '}
        <strong>Comissão Nacional de Proteção de Dados (CNPD)</strong> —{' '}
        <a href="https://www.cnpd.pt">www.cnpd.pt</a> — or with the authority of your country of
        residence.
      </p>
    </section>

    <section>
      <h2>8. Security</h2>
      <p>
        The platform is served exclusively over an encrypted connection (HTTPS). Passwords are
        stored hashed, never in readable form. Backups are encrypted before leaving the server.
        Administrative access is limited to those strictly necessary to operate the service.
      </p>
      <p>
        No system is entirely immune. If a data breach occurs that is likely to result in a high
        risk to your rights, we will notify you and the CNPD as legally required.
      </p>
    </section>

    <section>
      <h2>9. Cookies</h2>
      <p>
        We use only cookies strictly necessary for the platform to function — in particular, to keep
        you signed in. We do not use advertising or behavioural analytics cookies, and therefore
        display no consent request.
      </p>
    </section>

    <section>
      <h2>10. Changes to this policy</h2>
      <p>
        We may update this policy. The date of the last update appears at the top. Changes with
        material impact are communicated by email to registered users at least 15 days in advance.
      </p>
    </section>

    <section>
      <h2>11. Contact</h2>
      <p>
        <strong>Leonardo Ferreira</strong> — <a href="mailto:info@roadbase.eu">info@roadbase.eu</a>
      </p>
    </section>
  </>
);
